/**
 * GoogleAnalytics.js - FIXED VERSION
 * ===================================
 *
 * Location: frontend/src/utils/GoogleAnalytics.js
 */

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_ID || 'G-EEZGV02CCJ';

// ============================================
// CORE FUNCTIONS
// ============================================

/**
 * Check cookie consent before tracking (GDPR)
 */
const hasConsent = () => {
  const consent = localStorage.getItem('cookieConsent');
  return consent === 'accepted' || consent === 'true';
};

/**
 * Check if GA is available and user consented
 */
const isGAAvailable = () => {
  return typeof window !== 'undefined' &&
         window.gtag &&
         GA_MEASUREMENT_ID &&
         hasConsent();
};

/**
 * Initialize Google Analytics
 */
export const initGA = () => {
  if (typeof window !== 'undefined' && window.gtag && GA_MEASUREMENT_ID) {
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: window.location.pathname,
      send_page_view: false,
      anonymize_ip: true
    });
    console.log('✅ Google Analytics initialized with ID:', GA_MEASUREMENT_ID);
  }
};

/**
 * Track page views
 */
export const pageview = (url, title = '') => {
  if (isGAAvailable()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
      page_title: title
    });
  }
};

/**
 * Generic event tracking
 */
export const event = ({ action, category, label, value }) => {
  if (isGAAvailable()) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// ============================================
// PAGE & NAVIGATION TRACKING
// ============================================

/**
 * Track page view with custom name
 */
export const trackPageView = (pageName, pageTitle = '') => {
  pageview(window.location.pathname, pageTitle || pageName);

  event({
    action: 'page_view',
    category: 'Navigation',
    label: pageName,
  });
};

/**
 * Track category selection
 */
export const trackCategorySelect = (category) => {
  event({
    action: 'category_select',
    category: 'Navigation',
    label: category,
  });
};

// ============================================
// USER TRACKING
// ============================================

/**
 * Track user signup
 */
export const trackSignup = (method = 'email') => {
  event({
    action: 'sign_up',
    category: 'User',
    label: method,
  });

  // Also track as conversion
  if (isGAAvailable()) {
    window.gtag('event', 'conversion', {
      send_to: GA_MEASUREMENT_ID,
      event_category: 'User',
      event_label: 'signup'
    });
  }
};

/**
 * Track login
 */
export const trackLogin = (method = 'email') => {
  event({
    action: 'login',
    category: 'User',
    label: method,
  });
};

/**
 * Track logout
 */
export const trackLogout = () => {
  event({
    action: 'logout',
    category: 'User',
    label: 'user_logout',
  });
};

/**
 * Set user properties (for logged in users)
 */
export const setUserProperties = (properties) => {
  if (isGAAvailable()) {
    window.gtag('set', 'user_properties', properties);
  }
};

/**
 * Set user ID
 */
export const setUserId = (userId) => {
  if (isGAAvailable()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId
    });
  }
};

// ============================================
// BENCHMARK TRACKING
// ============================================

/**
 * Track benchmark/comparison runs
 */
export const trackBenchmarkRun = (category, models, credits_used = 0) => {
  const modelList = Array.isArray(models) ? models.join(', ') : models;
  event({
    action: 'benchmark_run',
    category: 'Benchmarking',
    label: `${category} - ${modelList}`,
    value: credits_used
  });
};

/**
 * Track benchmark completed successfully
 */
export const trackBenchmarkComplete = (category, modelCount, runId) => {
  event({
    action: 'benchmark_complete',
    category: 'Benchmarking',
    label: `${category} - ${modelCount} models`,
  });

  if (isGAAvailable()) {
    window.gtag('event', 'benchmark_complete', {
      benchmark_category: category,
      model_count: modelCount,
      run_id: runId
    });
  }
};

/**
 * Track comparison view (when user views results)
 */
export const trackComparisonView = (category, model_count = 2) => {
  event({
    action: 'comparison_view',
    category: 'Engagement',
    label: category,
    value: model_count
  });
};

/**
 * Track model selection
 */
export const trackModelSelect = (model_id, category) => {
  event({
    action: 'model_select',
    category: 'Model Selection',
    label: `${model_id} - ${category}`,
  });
};

// ============================================
// ENGAGEMENT TRACKING
// ============================================

/**
 * Track vote action
 */
export const trackVote = (model_name, category = '') => {
  event({
    action: 'vote',
    category: 'Engagement',
    label: category ? `${model_name} in ${category}` : model_name,
  });
};

/**
 * Track share action
 */
export const trackShare = (method, contentType, itemId = '') => {
  event({
    action: 'share',
    category: 'Social',
    label: `${method} - ${contentType}`,
  });

  if (isGAAvailable()) {
    window.gtag('event', 'share', {
      method: method,
      content_type: contentType,
      item_id: itemId
    });
  }
};

/**
 * Track filter usage
 */
export const trackFilter = (filterType, filterValue) => {
  event({
    action: 'filter',
    category: 'Engagement',
    label: `${filterType}: ${filterValue}`,
  });
};

/**
 * Track sort usage
 */
export const trackSort = (sortType) => {
  event({
    action: 'sort',
    category: 'Engagement',
    label: sortType,
  });
};

/**
 * Track search
 */
export const trackSearch = (searchTerm) => {
  if (isGAAvailable()) {
    window.gtag('event', 'search', {
      search_term: searchTerm
    });
  }
};

/**
 * Track privacy toggle
 */
export const trackPrivacyToggle = (runId, isPublic) => {
  event({
    action: 'privacy_toggle',
    category: 'Privacy',
    label: isPublic ? 'make_public' : 'make_private',
  });
};

// ============================================
// EXPORT & DOWNLOAD TRACKING
// ============================================

/**
 * Track download action
 */
export const trackDownload = (type, category) => {
  event({
    action: 'download',
    category: 'Export',
    label: `${type} - ${category}`,
  });
};

/**
 * Track data export
 */
export const trackExport = (exportType, format) => {
  event({
    action: 'export',
    category: 'Export',
    label: `${exportType}_${format}`,
  });
};

// ============================================
// E-COMMERCE / MONETIZATION TRACKING
// ============================================

/**
 * Track upgrade click (before payment)
 */
export const trackUpgradeClick = (plan, billing = 'monthly') => {
  event({
    action: 'upgrade_click',
    category: 'Premium',
    label: `${plan}_${billing}`,
  });
};

/**
 * Track view item (pricing page)
 */
export const trackViewItem = (itemId, itemName, price) => {
  if (isGAAvailable()) {
    window.gtag('event', 'view_item', {
      currency: 'USD',
      value: price,
      items: [{
        item_id: itemId,
        item_name: itemName,
        price: price
      }]
    });
  }
};

/**
 * Track begin checkout
 */
export const trackBeginCheckout = (plan, price, billing) => {
  if (isGAAvailable()) {
    window.gtag('event', 'begin_checkout', {
      currency: 'USD',
      value: price,
      items: [{
        item_id: plan,
        item_name: `${plan} Plan`,
        item_category: 'subscription',
        item_variant: billing,
        price: price,
        quantity: 1
      }]
    });
  }

  event({
    action: 'begin_checkout',
    category: 'Premium',
    label: `${plan}_${billing}`,
    value: price
  });
};

/**
 * Track successful upgrade/purchase
 */
export const trackUpgradeSuccess = (plan, billing = 'monthly', value = 0) => {
  event({
    action: 'purchase',
    category: 'Premium',
    label: `${plan}_${billing}`,
    value: value
  });

  // Track as conversion
  if (isGAAvailable()) {
    window.gtag('event', 'purchase', {
      transaction_id: Date.now().toString(),
      value: value,
      currency: 'USD',
      items: [{
        item_id: plan,
        item_name: `${plan} Plan`,
        item_category: 'subscription',
        item_variant: billing,
        price: value,
        quantity: 1
      }]
    });
  }
};

/**
 * Track purchase (alias for trackUpgradeSuccess)
 */
export const trackPurchase = (transactionId, plan, price, billing) => {
  if (isGAAvailable()) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      currency: 'USD',
      value: price,
      items: [{
        item_id: plan,
        item_name: `${plan} Plan`,
        item_category: 'subscription',
        item_variant: billing,
        price: price,
        quantity: 1
      }]
    });
  }
};

/**
 * Track low credits warning shown
 */
export const trackLowCredits = (remaining_credits) => {
  event({
    action: 'low_credits_warning',
    category: 'Monetization',
    label: 'credits_warning_shown',
    value: remaining_credits
  });
};

// ============================================
// ERROR TRACKING
// ============================================

/**
 * Track error occurrences
 */
export const trackError = (error_type, details = '') => {
  event({
    action: 'error',
    category: 'Errors',
    label: `${error_type}: ${details.substring(0, 100)}`,
  });
};

// ============================================
// GENERIC EVENT TRACKING
// ============================================

/**
 * Track any custom event
 */
export const trackEvent = (category, action, label = null, value = null) => {
  event({
    action: action,
    category: category,
    label: label,
    value: value
  });
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if analytics consent was given
 */
export const hasAnalyticsConsent = () => hasConsent();

// ============================================
// DEFAULT EXPORT
// ============================================

export default {
  // Core
  initGA,
  pageview,
  event,

  // Page & Navigation
  trackPageView,
  trackCategorySelect,

  // User
  trackSignup,
  trackLogin,
  trackLogout,
  setUserProperties,
  setUserId,

  // Benchmarking
  trackBenchmarkRun,
  trackBenchmarkComplete,
  trackComparisonView,
  trackModelSelect,

  // Engagement
  trackVote,
  trackShare,
  trackFilter,
  trackSort,
  trackSearch,
  trackPrivacyToggle,

  // Export
  trackDownload,
  trackExport,

  // E-commerce
  trackUpgradeClick,
  trackViewItem,
  trackBeginCheckout,
  trackUpgradeSuccess,
  trackPurchase,
  trackLowCredits,

  // Errors
  trackError,

  // Generic
  trackEvent,

  // Utility
  hasAnalyticsConsent
};